import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AXIS_LINE, AXIS_TICK, ChartCard, GRID } from '@/pages/GamAdvertisers/components/charts';
import type { NavHour } from '@/types/navigation-report';
import { fmtNav } from './format';

interface HourlyChartProps {
    hourly: NavHour[];
    totalSessions: number;
}

function HourTooltip({ active, payload }: any) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload as { label: string; sessions: number; share_pct: number };
    return (
        <div className="rounded-lg border border-slate-700 bg-slate-800 p-3 text-white shadow-xl">
            <p className="mb-1 text-xs font-semibold">{p.label} (Brasil)</p>
            <p className="text-xs">{fmtNav.num(p.sessions)} sessões · {fmtNav.pct1(p.share_pct)} das entradas</p>
        </div>
    );
}

/** Em que horas (Brasil) as sessões entraram por esta P1. A hora mais forte fica destacada. */
export function HourlyChart({ hourly, totalSessions }: HourlyChartProps) {
    const data = useMemo(
        () => hourly.map((h) => ({
            label: fmtNav.hour(h.hour),
            sessions: h.sessions,
            share_pct: totalSessions > 0 ? Math.round((h.sessions / totalSessions) * 1000) / 10 : 0,
        })),
        [hourly, totalSessions]
    );
    const max = Math.max(0, ...data.map((d) => d.sessions));
    const peak = data.find((d) => d.sessions === max && max > 0);

    return (
        <ChartCard title="Horários de acesso" subtitle={peak ? `Pico às ${peak.label} (hora do Brasil) com ${fmtNav.num(peak.sessions)} sessões` : 'Hora do Brasil'}>
            <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                        <CartesianGrid {...GRID} vertical={false} />
                        <XAxis dataKey="label" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} interval={1} />
                        <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip content={<HourTooltip />} cursor={{ fill: 'rgba(100,116,139,0.12)' }} />
                        <Bar dataKey="sessions" name="Sessões" radius={[3, 3, 0, 0]}>
                            {data.map((d) => (
                                <Cell key={d.label} fill={d.sessions === max && max > 0 ? '#f59e0b' : '#3b82f6'} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </ChartCard>
    );
}
